import React, { useState, useEffect } from 'react';
import { libraryService, userService } from '../../services/supabase';
import { User } from '../../types/models';
import { FiPlus, FiSearch, FiX, FiCheck, FiUser } from 'react-icons/fi';

interface UserAccessManagerProps {
  libraryId: string;
}

const UserAccessManager: React.FC<UserAccessManagerProps> = ({ libraryId }) => {
  const [loading, setLoading] = useState(false);
  const [usersWithAccess, setUsersWithAccess] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (libraryId) {
      fetchUsersWithAccess();
      fetchAllUsers();
    }
  }, [libraryId]);

  // Busca os usuários que já têm acesso à biblioteca
  const fetchUsersWithAccess = async () => {
    if (!libraryId) return;

    try {
      setLoading(true);
      const { data, error } = await libraryService.getUsersWithAccess(libraryId);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Extrai os usuários da resposta
      const users = data?.map((item: any) => item.users) || [];
      setUsersWithAccess(users);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar usuários com acesso');
      console.error('Erro ao buscar usuários com acesso:', err);
    } finally {
      setLoading(false);
    }
  };

  // Busca todos os usuários para adicionar
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await userService.getUsers();
      
      if (error) {
        throw new Error(error.message);
      }
      
      setAllUsers(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar todos os usuários:', err);
    } finally {
      setLoading(false);
    }
  };

  // Adiciona acesso a um usuário
  const handleAddAccess = async (userId: string) => {
    try {
      setLoading(true);
      const { error } = await libraryService.grantLibraryAccess(libraryId, userId);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Atualiza a lista de usuários com acesso
      fetchUsersWithAccess();
    } catch (err: any) {
      setError(err.message || 'Erro ao adicionar acesso');
      console.error('Erro ao adicionar acesso:', err);
    } finally {
      setLoading(false);
    }
  };

  // Remove acesso de um usuário
  const handleRemoveAccess = async (userId: string) => {
    try {
      setLoading(true);
      const { error } = await libraryService.revokeLibraryAccess(libraryId, userId);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Atualiza a lista de usuários com acesso
      setUsersWithAccess(usersWithAccess.filter(user => user.id !== userId));
    } catch (err: any) {
      setError(err.message || 'Erro ao remover acesso');
      console.error('Erro ao remover acesso:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtra usuários para adicionar (que ainda não têm acesso)
  const filteredUsers = allUsers.filter(user => 
    !usersWithAccess.some(accessUser => accessUser.id === user.id) &&
    (user.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
     user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-md p-3 mb-3 flex items-center text-red-200 text-sm">
          <FiX className="mr-2 flex-shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Lista de usuários com acesso */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-300">Usuários com acesso</h3>
          <span className="bg-gray-700 text-xs text-gray-300 px-2 py-1 rounded-full">
            {usersWithAccess.length}
          </span>
        </div>
        
        <div className="max-h-[200px] overflow-y-auto">
          {loading && usersWithAccess.length === 0 ? (
            <div className="flex justify-center items-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-vermelho-claro"></div>
            </div>
          ) : usersWithAccess.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              Nenhum usuário com acesso a esta biblioteca
            </div>
          ) : (
            <ul className="divide-y divide-gray-700">
              {usersWithAccess.map(user => (
                <li key={user.id} className="px-4 py-3 flex justify-between items-center hover:bg-gray-800/50">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-300">
                      <FiUser size={14} />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-200">{user.nome}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveAccess(user.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                    disabled={loading}
                  >
                    <FiX size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Buscar usuários para adicionar */}
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-gray-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-500" size={14} />
            </div>
            <input
              type="text"
              placeholder="Buscar usuários para adicionar..."
              className="input pl-9 py-2 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {searchTerm && (
          <div className="max-h-[150px] overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                Nenhum usuário encontrado
              </div>
            ) : (
              <ul className="divide-y divide-gray-700">
                {filteredUsers.map(user => (
                  <li 
                    key={user.id} 
                    className="px-4 py-2 flex justify-between items-center hover:bg-gray-800 cursor-pointer"
                    onClick={() => handleAddAccess(user.id)}
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-300">
                        <FiUser size={14} />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-200">{user.nome}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                    <FiPlus className="text-blue-400" size={18} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAccessManager; 